/**
 * The /looker date presets as exact ranges. Pure, no I/O, relative imports
 * only (api/ imports this file).
 *
 * Every preset ends YESTERDAY in Bangkok (today's numbers are still moving),
 * and each one has a previous period of equal length for Δ% — "this month"
 * compares with the same days of last month. A preset travels as a token
 * ('28d', 'mtd', 'custom:A:B'); the server turns it into an explicit Looker
 * range, so the result never depends on Looker's own timezone.
 *
 * @module services/looker/lookerDateRange
 */

export type DatePresetId = '7d' | '14d' | '28d' | '30d' | 'mtd' | 'last_month';
export const DATE_PRESET_IDS: readonly DatePresetId[] = ['7d', '14d', '28d', '30d', 'mtd', 'last_month'];
export const DEFAULT_DATE_PRESET: DatePresetId = '28d';
/** What a builder template stores in Looker for the default: valid in Looker's own UI, read back as '28d'. */
export const LOOKER_DEFAULT_DATE_EXPRESSION = '28 days ago for 28 days';

export interface DateRange {
  /** Inclusive, YYYY-MM-DD. */
  start: string;
  end: string;
}

export interface ResolvedDate {
  /** What the page shows as selected. */
  token: string;
  /** What the Looker filter gets. */
  expression: string;
  /** null = no bounded range (all time, a Looker expression we do not interpret, an empty month). */
  range: DateRange | null;
  previous: DateRange | null;
}

const DAY_MS = 86_400_000;
const MAX_CUSTOM_DAYS = 3 * 366;
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const N_DAYS: Record<string, DatePresetId> = { '7': '7d', '14': '14d', '28': '28d', '30': '30d' };

const isRealDate = (d: string): boolean => {
  if (!ISO.test(d)) return false;
  const t = Date.parse(`${d}T00:00:00Z`);
  return Number.isFinite(t) && new Date(t).toISOString().slice(0, 10) === d;
};

export const addDays = (day: string, n: number): string =>
  new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10);

export const rangeDays = (r: DateRange): number =>
  Math.round((Date.parse(`${r.end}T00:00:00Z`) - Date.parse(`${r.start}T00:00:00Z`)) / DAY_MS) + 1;

/** Looker's "A to B" excludes B. */
export const toLookerExpression = (r: DateRange): string => `${r.start} to ${addDays(r.end, 1)}`;

export const bangkokToday = (now: Date = new Date()): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);

export const customToken = (start: string, end: string): string => `custom:${start}:${end}`;

const firstOfMonth = (day: string): string => `${day.slice(0, 7)}-01`;
const lastOfPreviousMonth = (day: string): string => addDays(firstOfMonth(day), -1);

/** The span before `r`, ending the day before it starts. */
const shiftBack = (r: DateRange): DateRange => ({ start: addDays(r.start, -rangeDays(r)), end: addDays(r.start, -1) });

const resolved = (token: string, range: DateRange, previous: DateRange): ResolvedDate =>
  ({ token, expression: toLookerExpression(range), range, previous });

const preset = (id: DatePresetId, today: string): ResolvedDate => {
  const yesterday = addDays(today, -1);
  if (id === 'mtd') {
    const start = firstOfMonth(today);
    if (start > yesterday) return { token: id, expression: 'NULL', range: null, previous: null };
    const range = { start, end: yesterday };
    const prevStart = firstOfMonth(lastOfPreviousMonth(today));
    const prevEnd = addDays(prevStart, rangeDays(range) - 1);
    const prevMonthEnd = lastOfPreviousMonth(today);
    return resolved(id, range, { start: prevStart, end: prevEnd > prevMonthEnd ? prevMonthEnd : prevEnd });
  }
  if (id === 'last_month') {
    const end = lastOfPreviousMonth(today);
    const range = { start: firstOfMonth(end), end };
    const prevEnd = lastOfPreviousMonth(end);
    return resolved(id, range, { start: firstOfMonth(prevEnd), end: prevEnd });
  }
  const days = Number(id.slice(0, -1));
  const range = { start: addDays(today, -days), end: yesterday };
  return resolved(id, range, shiftBack(range));
};

/**
 * The server's rules for a custom range, in one place: real dates, start not
 * after end, end before today, at most MAX_CUSTOM_DAYS days. The filter bar
 * uses it to keep Apply disabled for a range the server would answer 400.
 */
export const isValidCustomRange = (from: string, to: string, today: string): boolean => {
  if (!isRealDate(from) || !isRealDate(to)) return false;
  return from <= to && to < today && rangeDays({ start: from, end: to }) <= MAX_CUSTOM_DAYS;
};

/** A filter value as a range. null = a custom range the server must refuse (400). */
export const resolveDateValue = (value: string, today: string): ResolvedDate | null => {
  const v = value.trim();
  if (v === '') return { token: '', expression: '', range: null, previous: null };
  if ((DATE_PRESET_IDS as readonly string[]).includes(v)) return preset(v as DatePresetId, today);

  const native = /^(\d+) days ago for (\d+) days$/i.exec(v);
  if (native && native[1] === native[2] && N_DAYS[native[1]]) return preset(N_DAYS[native[1]], today);

  if (v.startsWith('custom:')) {
    const [, start, end] = v.split(':');
    if (!start || !end || !isValidCustomRange(start, end, today)) return null;
    const range = { start, end };
    return resolved(customToken(start, end), range, shiftBack(range));
  }
  return { token: v, expression: v, range: null, previous: null };
};

/** Result and Cost per Result leave out the last 3 days: conversions there are still arriving (D1 decision 4). */
export const RESULT_LAG_DAYS = 3;

export interface ResultWindow {
  /** The date filter value for Result queries. 'NULL' = no day qualifies (Looker returns no rows). */
  expression: string;
  /** The period Result is compared with; null = no Δ. */
  previous: DateRange | null;
  /** `previous` as a Looker expression; null = no Δ. */
  previousExpression: string | null;
  /** The last day Result counts (inclusive). */
  through: string;
}

const MONTH_ALIGNED = new Set(['mtd', 'last_month']);

/** The first `rangeDays(cut)` days of the page's previous month, never past its end. */
const sameDaysOf = (previous: DateRange, cut: DateRange): DateRange => {
  const end = addDays(previous.start, rangeDays(cut) - 1);
  return { start: previous.start, end: end > previous.end ? previous.end : end };
};

/** The period a range cut short is compared with: equal length, so Δ% compares like with like. */
const cutPreviousOf = (r: ResolvedDate, cut: DateRange): DateRange =>
  MONTH_ALIGNED.has(r.token) && r.previous ? sameDaysOf(r.previous, cut) : shiftBack(cut);

/**
 * The selected range cut to end at today − RESULT_LAG_DAYS, and the period it
 * is compared with. A range that needs no cut keeps the page's own previous
 * period (so "last month" compares with the whole month before, like every
 * other Δ). A cut range compares with an equal-length one: month presets with
 * the same days of the month before, any other range with the days
 * immediately before it. null = a free-text Looker expression the app cannot
 * cut (its Result is shown as "—", never uncut).
 */
export const resultWindowOf = (r: ResolvedDate, today: string): ResultWindow | null => {
  const through = addDays(today, -RESULT_LAG_DAYS);
  const none = { previous: null, previousExpression: null };
  if (r.range) {
    if (r.range.end <= through) {
      return { expression: r.expression, previous: r.previous, previousExpression: r.previous ? toLookerExpression(r.previous) : null, through: r.range.end };
    }
    if (r.range.start > through) return { expression: 'NULL', ...none, through };
    const range = { start: r.range.start, end: through };
    const previous = cutPreviousOf(r, range);
    return { expression: toLookerExpression(range), previous, previousExpression: toLookerExpression(previous), through };
  }
  if (r.expression === '') return { expression: `before ${addDays(through, 1)}`, ...none, through };
  if (r.expression === 'NULL') return { expression: 'NULL', ...none, through };
  return null;
};
